<template>
    <span>
        <span>
            <span @click.prevent="clickElement" v-if="isHtml == false">
                <slot>{{ textMessage }}</slot>
            </span>
            <span
                @click.prevent="clickElement"
                @keydown.enter.prevent="clickElement"
                @keydown.space.prevent="clickElement"
                v-else-if="isHtml == true"
                v-html="renderedHtml"
            >
            </span>
        </span>
    </span>
</template>

<script setup>
import { ref, onMounted, watch, watchEffect, nextTick, computed } from 'vue'
import router from '../router'
import { useSystemStore } from '@/stores/system'
import { useTranslationStore } from '@/stores/translation'
import { useCurrentElement } from '@vueuse/core'
import EligibleProducts from '@/views/EligibleProducts.vue';

const emit = defineEmits(['reload-data', 'custom-event'])

const systemStore = useSystemStore()
const translationStore = useTranslationStore()
const el = useCurrentElement()
const selectedTab = ref(0)
const selectedDct = ref(null)
const search = ref('')
const page = ref('')
const note = ref('')
const showSaveLink = ref(false)

const props = defineProps({
    elId: { type: String, default: '' },
    position: { type: String, default: 'absolute' },
    defaultText: { type: String, default: '', required: true },
    isHtml: { type: Boolean, default: false },
    elementVisible: { type: Boolean, default: true },
    findAndReplace: { type: Array, default: [] }
})


const dictionaryHeaders = ref([
    { title: "", key: "link" },
    { title: "Id", value: "id" },
    { title: "English", value: "en" },
    { title: "Elements Linked", key: "elementsLinked" }
]);

const elId = ref(0)
const position = ref('absolute')
const textMessage = ref('')
const defaultText = ref('')
const isHtml = ref(false)
const elementVisible = ref(true)

const renderedHtml = computed(() => {
    if (isHtml.value !== true) {
        return ''
    }

    const html = textMessage.value ?? ''
    return ensureRouteElementsAreFocusable(html)
})

const ensureRouteElementsAreFocusable = (html) => {
    if (!html || typeof html !== 'string') {
        return html
    }

    // `v-html` inserts raw HTML (not Vue components). Custom `<route>` tags are not focusable by default,
    // so make them keyboard tabbable and give them link semantics unless explicitly provided.
    return html.replace(/<route\b([^>]*?)>/gi, (match, attrs) => {
        const hasTabindex = /\btabindex\s*=/.test(attrs)
        const hasRole = /\brole\s*=/.test(attrs)

        let nextAttrs = attrs ?? ''
        if (nextAttrs.length && !/\s$/.test(nextAttrs)) {
            nextAttrs += ' '
        }

        if (!hasTabindex) {
            nextAttrs += 'tabindex="0" '
        }

        if (!hasRole) {
            nextAttrs += 'role="link" '
        }

        nextAttrs = nextAttrs.trimEnd()
        return `<route${nextAttrs ? ' ' + nextAttrs.trim() : ''}>`
    })
}

onMounted(() => {
    nextTick(() => {
        getDictionaryLink()
    });

    elId.value = props.elId
    position.value = props.position
    defaultText.value = props.defaultText
    textMessage.value = props.defaultText // getTranslatedText(props.elId)
    isHtml.value = props.isHtml
    elementVisible.value = props.elementVisible

})

watch(
    () => systemStore.selectedLanguage, (val) => {
        getDictionaryLink()
    }
)

watch(
    () => systemStore.defaultDataLoaded, (val) => {
        if (val == true){
            getDictionaryLink()
        } 
    }
)

const getDictionaryLink = () => {
    if (systemStore.defaultDataLoaded === true) {

        textMessage.value = defaultText.value
        var dct = systemStore.languageDictionary.filter(ld => {
            return ld.en.toLowerCase() == defaultText.value.toLowerCase()
        })

        if (dct.length > 0) {
            textMessage.value = dct[0][systemStore.selectedLanguage]
        } else {
            textMessage.value = defaultText.value

            
            // If the default text is not found in the dictionary, we can add it
            // to the dictionary if it is not empty and we are not on the login page or registration page (not authenticated)
            if (defaultText.value.trim() != '' && router.currentRoute.value.name != 'login' && router.currentRoute.value.name != 'registration') {
                translationStore.upsertTranslationDictionaryItem(defaultText.value)
            }
        }

        props.findAndReplace.forEach(element => {
            textMessage.value = textMessage.value.replace(element.find, element.replace)                        
        });
    }
}

watch(
    () => props.elementVisible, (val) => {
        getDictionaryLink()
    }
)
watchEffect(
    () => {
        // console.log(props.elementVisible)
    }
)



const clickElement = (ev) => {
    const target = ev?.target
    if (!target) return

    const anchor = target.closest?.('a')
    if (anchor) {
        const href = anchor.getAttribute('href')
        if (href) {
            window.open(href, '_blank')?.focus()
        }
        return
    }

    const routeEl = target.closest?.('route')
    if (routeEl) {
        const rn = routeEl.getAttribute('route-name')
        if (rn) {
            router.push({ name: rn })
        }
        return
    }

    const customEventEl = target.closest?.('custom-event')
    if (customEventEl) {
        const action = customEventEl.getAttribute('action')
        if (action) {
            triggerEvent(action)
        }
        return
    }
}

const triggerEvent = (action) => {
    emit('custom-event', action)
}



</script>

<style>
.loading-enter-active {
    animation: enter 5.2s ease-out;
}

.loading-leave-active {
    animation: enter 5.2s ease-in reverse;
}

@keyframes enter {
    0% {
        opacity: 0%;
        transform: scale(0);
    }

    100% {
        opacity: 100%;
        transform: scale(1);
    }
}

.modal-enter-active,
.modal-leave-active {
    transition: all 2.25s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
    transform: scale(1.1);
}
</style>